import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Users, Building, User, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/shared/PageTitle';
import { useTranslation } from 'react-i18next';
import { useGetVendorMeetingsQuery } from '../../store/vendorApi';
import { Meeting } from '@clareefai/types';
import MeetingDetailsModal from '../../components/meetings/MeetingDetailsModal';

const Meetings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Calculate date range for the week (7 days centered around selected date)
  const dateRange = useMemo(() => {
    const dates = [];
    const startDate = new Date(selectedDate);
    startDate.setDate(startDate.getDate() - 3); // 3 days before
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [selectedDate]);

  // Calculate start and end dates for API query (week range)
  const { startDate: apiStartDate, endDate: apiEndDate } = useMemo(() => {
    const start = new Date(dateRange[0]);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateRange[dateRange.length - 1]);
    end.setHours(23, 59, 59, 999);
    
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, [dateRange]);

  // Fetch meetings for the date range
  const { data: meetings = [], isLoading, isSuccess, error } = useGetVendorMeetingsQuery({
    startDate: apiStartDate,
    endDate: apiEndDate,
  });

  // Filter meetings by selected date
  const meetingsForSelectedDate = useMemo(() => {
    const dateStr = selectedDate.toISOString().split('T')[0];
    return meetings.filter(meeting => meeting.date === dateStr);
  }, [meetings, selectedDate]);


  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };

  const formatDateForDisplay = (date: Date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t('meetings.today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return t('meetings.tomorrow');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('meetings.yesterday');
    } else {
      const locale = 'en-US';
      return date.toLocaleDateString(locale, { 
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    }
  };

  const getDayNumber = (date: Date) => {
    return date.getDate();
  };

  const getWeekday = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const getMeetingsCountForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return meetings.filter(meeting => meeting.date === dateStr).length;
  };

  return (
    <div className="space-y-6">
      <PageTitle title="Meetings" />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Meetings Management</h1>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigateDate('prev')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <span className="text-sm font-medium text-gray-700 px-3">
            {selectedDate.toLocaleDateString('en-US', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </span>
          <button
            onClick={() => navigateDate('next')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Barre de navigation par date */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Date Navigation</h2>
          <div className="flex items-center text-sm text-gray-500">
            <Calendar className="w-4 h-4 mr-1" />
            Select a date
          </div>
        </div>
        
        <div className="grid grid-cols-7 gap-2">
          {dateRange.map((date, index) => {
            const isSelected = date.toDateString() === selectedDate.toDateString();
            const isToday = date.toDateString() === new Date().toDateString();
            const meetingsCount = getMeetingsCountForDate(date);
            
            return (
              <button
                key={index}
                onClick={() => setSelectedDate(date)}
                className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-center">
                  <div className={`text-xs font-medium mb-1 ${
                    isSelected ? 'text-purple-600' : 'text-gray-500'
                  }`}>
                    {getWeekday(date)}
                  </div>
                  <div className={`text-2xl font-bold mb-1 ${
                    isSelected ? 'text-purple-900' : 
                    isToday ? 'text-blue-600' : 'text-gray-900'
                  }`}>
                    {getDayNumber(date)}
                  </div>
                  {meetingsCount > 0 && (
                    <div className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-medium ${
                      isSelected ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                    }`}>
                      {meetingsCount}
                    </div>
                  )}
                </div>
                {isToday && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
        
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            <span className="font-medium">{formatDateForDisplay(selectedDate)}</span>
            {meetingsForSelectedDate.length > 0 ? (
              <span> - {meetingsForSelectedDate.length} meeting{meetingsForSelectedDate.length > 1 ? 's' : ''}</span>
            ) : (
              <span> - No meetings scheduled</span>
            )}
          </p>
        </div>
      </div>

      {/* Kanban board for selected date */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">
            {formatDateForDisplay(selectedDate)} - Meetings Schedule
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {meetingsForSelectedDate.length} meeting{meetingsForSelectedDate.length > 1 ? 's' : ''} scheduled
          </p>
        </div>
        
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading meetings...</p>
          </div>
        ) : error && !isSuccess ? (
          <div className="p-12 text-center">
            <p className="text-red-600 mb-2">Error loading meetings</p>
            <p className="text-gray-600 text-sm">Please try again later</p>
          </div>
        ) : meetingsForSelectedDate.length > 0 ? (
          <div className="p-6">
            <div className="space-y-4">
              {meetingsForSelectedDate.map((meeting) => {
                
                return (
                  <div key={meeting.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Meeting {meeting.time ? `at ${meeting.time}` : ''}
                          </h3>
                          {meeting.date && (
                            <p className="text-sm text-gray-600">
                              {new Date(meeting.date).toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className={`status-badge ${
                        meeting.status === 'request' ? 'status-invited' :
                        meeting.status === 'progress' ? 'status-review' :
                        meeting.status === 'booked' ? 'status-available' :
                        meeting.status === 'completed' ? 'status-completed' :
                        'status-failed'
                      }`}>
                        {meeting.status === 'request' ? 'Request' :
                         meeting.status === 'progress' ? 'In Progress' :
                         meeting.status === 'booked' ? 'Confirmed' :
                         meeting.status === 'completed' ? 'Completed' :
                         'To Reschedule'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Promoter Section - Clickable */}
                      <button
                        onClick={() => meeting.promoter_id && navigate(`/promoters/${meeting.promoter_id}`)}
                        className="bg-green-50 rounded-lg p-4 border border-green-200 hover:bg-green-100 hover:shadow-md transition-all cursor-pointer text-left"
                      >
                        <div className="flex items-center space-x-2 mb-3">
                          <Users className="w-5 h-5 text-green-600" />
                          <h4 className="font-semibold text-green-900">Promoter</h4>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{meeting.promoter_name || 'Unknown Promoter'}</h5>
                            {meeting.promoter_company && (
                              <p className="text-sm text-gray-600 flex items-center">
                                <Building className="w-3 h-3 mr-1" />
                                {meeting.promoter_company}
                              </p>
                            )}
                            {meeting.promoter_email && (
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="hover:text-green-600">{meeting.promoter_email}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Prospect Section - Clickable */}
                      <button
                        onClick={() => meeting.prospect_id && navigate(`/prospect/${meeting.prospect_id}`)}
                        className="bg-blue-50 rounded-lg p-4 border border-blue-200 hover:bg-blue-100 hover:shadow-md transition-all cursor-pointer text-left"
                      >
                        <div className="flex items-center space-x-2 mb-3">
                          <Target className="w-5 h-5 text-blue-600" />
                          <h4 className="font-semibold text-blue-900">Prospect</h4>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{meeting.prospect_name || 'Unknown Prospect'}</h5>
                            {meeting.prospect_company && (
                              <p className="text-sm text-gray-600 flex items-center">
                                <Building className="w-3 h-3 mr-1" />
                                {meeting.prospect_company}
                              </p>
                            )}
                            {meeting.prospect_email && (
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="hover:text-blue-600">{meeting.prospect_email}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Meeting Actions */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            {meeting.id && <span>ID: {meeting.id}</span>}
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedMeeting(meeting);
                                setIsModalOpen(true);
                              }}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No meetings scheduled
            </h3>
            <p className="text-gray-600">
              No appointments are scheduled for this date.
            </p>
          </div>
        )}
      </div>

      {/* Meeting Details Modal */}
      <MeetingDetailsModal
        meeting={selectedMeeting}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMeeting(null);
        }}
      />
    </div>
  );
};

export default Meetings;